<script setup lang="ts" generic="T">
import type { ComponentPublicInstance } from 'vue'
import {
  buildMasonryItemStyle,
  buildMasonryLayout,
  estimateMasonryItemHeight
} from '~/utils/masonry.mjs'

const MASONRY_LAYOUT_ANIMATION_MS = 560
const MASONRY_LAYOUT_EASING = 'cubic-bezier(0.33, 1, 0.68, 1)'

const props = withDefaults(defineProps<{
  items: T[]
  columns?: number
  tabletColumns?: number
  tabletBreakpoint?: number
  mobileBreakpoint?: number
  gap?: number
  estimatedHeight?: number
  estimatedHeights?: readonly number[]
}>(), {
  columns: 3,
  tabletColumns: 2,
  tabletBreakpoint: 900,
  mobileBreakpoint: 640,
  gap: 12,
  estimatedHeight: 260,
  estimatedHeights: () => []
})

const columnCount = ref(props.columns)
const rootElement = ref<HTMLElement | null>(null)
const rootWidth = ref(0)
const measuredHeights = ref<Record<number, number>>({})
const isLayoutAnimating = ref(false)
const itemElements = new Map<number, HTMLElement>()
let resizeObserver: ResizeObserver | null = null
let layoutAnimationReady = false
let layoutAnimationGeneration = 0
let activeLayoutAnimations: Animation[] = []

const itemKey = (item: unknown, index: number) => {
  if (item && typeof item === 'object') {
    const record = item as Record<string, unknown>
    return String(record.ID || record.id || record.slug || index)
  }
  return String(index)
}

const updateColumnCount = () => {
  if (!import.meta.client) return
  const width = window.innerWidth
  if (width <= props.mobileBreakpoint) {
    columnCount.value = 1
  } else if (width <= props.tabletBreakpoint) {
    columnCount.value = props.tabletColumns
  } else {
    columnCount.value = props.columns
  }
}

const columnWidth = computed(() => {
  const totalGap = props.gap * Math.max(0, columnCount.value - 1)
  return Math.max(0, rootWidth.value - totalGap) / columnCount.value
})

const heights = computed(() =>
  props.items.map((item, index) =>
    measuredHeights.value[index] ||
    estimateMasonryItemHeight(item, columnWidth.value, props.estimatedHeight, index, props.estimatedHeights)
  )
)

const layout = computed(() =>
  buildMasonryLayout(heights.value, columnCount.value, props.gap)
)

const rootStyle = computed(() => ({
  height: `${layout.value.height}px`
}))

const itemStyle = (index: number) => {
  const position = layout.value.items[index] || { column: 0, top: 0 }
  const fallbackHeight = props.estimatedHeights.length
    ? props.estimatedHeights[index % props.estimatedHeights.length]
    : props.estimatedHeight
  return {
    ...buildMasonryItemStyle(position, columnCount.value, props.gap),
    '--album-placeholder-height': `${fallbackHeight}px`
  }
}

const measureElement = (index: number, element: HTMLElement) => {
  const height = element.offsetHeight
  if (height > 0 && Math.abs((measuredHeights.value[index] || 0) - height) > 0.5) {
    measuredHeights.value = { ...measuredHeights.value, [index]: height }
  }
}

// measureItems 测量全部瀑布流卡片高度
const measureItems = () => {
  itemElements.forEach((element, index) => measureElement(index, element))
}

// 记录当前瀑布流卡片位置
const captureItemRects = () => new Map(
  Array.from(itemElements, ([index, element]) => [index, element.getBoundingClientRect()])
)

// 停止尚未结束的布局动画
const stopLayoutAnimations = () => {
  activeLayoutAnimations.forEach(animation => animation.cancel())
  activeLayoutAnimations = []
}

// 在新旧列布局之间同步过渡卡片位置和宽度
const animateColumnChange = async () => {
  const generation = ++layoutAnimationGeneration
  const shouldAnimate = layoutAnimationReady
  const beforeRects = captureItemRects()
  const beforeHeight = rootElement.value?.getBoundingClientRect().height || 0
  isLayoutAnimating.value = shouldAnimate
  stopLayoutAnimations()
  measuredHeights.value = {}
  await nextTick()
  measureItems()
  await nextTick()

  if (generation !== layoutAnimationGeneration) {
    return
  }

  if (!shouldAnimate || window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
    isLayoutAnimating.value = false
    return
  }

  const animations: Animation[] = []
  itemElements.forEach((element, index) => {
    const before = beforeRects.get(index)
    const after = element.getBoundingClientRect()
    if (!before || !after.width) {
      return
    }
    const animation = element.animate([
      {
        width: `${before.width}px`,
        transform: `translate3d(${before.left - after.left}px, ${before.top - after.top}px, 0)`
      },
      { width: `${after.width}px`, transform: 'none' }
    ], {
      duration: MASONRY_LAYOUT_ANIMATION_MS,
      easing: MASONRY_LAYOUT_EASING
    })
    animations.push(animation)
  })

  const root = rootElement.value
  if (root && beforeHeight > 0) {
    const afterHeight = root.getBoundingClientRect().height
    animations.push(root.animate([
      { height: `${beforeHeight}px` },
      { height: `${afterHeight}px` }
    ], {
      duration: MASONRY_LAYOUT_ANIMATION_MS,
      easing: MASONRY_LAYOUT_EASING
    }))
  }

  activeLayoutAnimations = animations
  await Promise.allSettled(animations.map(animation => animation.finished))
  if (generation === layoutAnimationGeneration) {
    activeLayoutAnimations = []
    isLayoutAnimating.value = false
    measureItems()
  }
}

const setItemRef = (element: Element | ComponentPublicInstance | null, index: number) => {
  const htmlElement = element instanceof HTMLElement ? element : null
  const previous = itemElements.get(index)
  if (previous && previous !== htmlElement) {
    resizeObserver?.unobserve(previous)
    itemElements.delete(index)
  }
  if (!htmlElement) return
  itemElements.set(index, htmlElement)
  resizeObserver?.observe(htmlElement)
  measureElement(index, htmlElement)
}

watch(
  () => props.items,
  async () => {
    measuredHeights.value = {}
    await nextTick()
    measureItems()
  }
)

watch(
  () => [props.columns, props.tabletColumns, props.tabletBreakpoint, props.mobileBreakpoint],
  () => updateColumnCount()
)

watch(columnCount, () => animateColumnChange())

onMounted(() => {
  updateColumnCount()
  rootWidth.value = rootElement.value?.getBoundingClientRect().width || 0
  resizeObserver = new ResizeObserver((entries) => {
    for (const entry of entries) {
      if (entry.target === rootElement.value) {
        rootWidth.value = entry.contentRect.width
        continue
      }
      // 宽度动画期间沿用目标高度，避免 ResizeObserver 反复重排
      if (isLayoutAnimating.value) {
        continue
      }
      const index = Number((entry.target as HTMLElement).dataset.masonryIndex)
      if (Number.isInteger(index)) {
        measureElement(index, entry.target as HTMLElement)
      }
    }
  })
  if (rootElement.value) {
    resizeObserver.observe(rootElement.value)
  }
  itemElements.forEach(element => resizeObserver?.observe(element))
  window.addEventListener('resize', updateColumnCount, { passive: true })
  nextTick(() => {
    layoutAnimationReady = true
  })
})

onBeforeUnmount(() => {
  layoutAnimationGeneration += 1
  stopLayoutAnimations()
  resizeObserver?.disconnect()
  window.removeEventListener('resize', updateColumnCount)
})
</script>

<template>
  <div
    ref="rootElement"
    class="blog-masonry"
    :style="rootStyle"
  >
    <div
      v-for="(item, index) in props.items"
      :key="itemKey(item, index)"
      :ref="element => setItemRef(element, index)"
      class="blog-masonry-item"
      :data-masonry-index="index"
      :style="itemStyle(index)"
    >
      <slot :item="item" :index="index" />
    </div>
  </div>
</template>

<style scoped>
.blog-masonry {
  position: relative;
  width: 100%;
}

.blog-masonry-item {
  position: absolute;
}
</style>
